import useTripStore from '../store/useTripStore';
import { useTripDesignerConfig } from '../config/TripDesignerConfigProvider';
import type { HotelPriceOverride } from '../config/TripDesignerConfigProvider';

interface GlobalTripPriceResult {
  priceLoading: boolean;
  globalTripPrice: number;
  globalTripCurrency: string | null;
  // The trip can't be added up (two currencies), once every price is in:
  // what disables saving. Same currencies as the total, so the button and
  // the header never disagree.
  hasCurrencyMismatch: boolean;
  participants: number;
  hasNoProducts: boolean;
}

function resolveExperiencePricing(day: any, experience: any) {
  return (
    day?.tripPrice ??
    day?.experiencePricing ??
    experience?.tripPrice ??
    experience?.price
  );
}

function resolveHotelPricing(
  day: any,
  hotel: any,
  getHotelPriceOverride?: (hotel: any) => HotelPriceOverride | undefined,
) {
  const override = getHotelPriceOverride?.(hotel);
  if (override) {
    // The total counts a stay once, so it takes the stay's price.
    return { ...override, price: override.stayPrice ?? override.price };
  }
  return day?.hotelPricing ?? hotel;
}

function addHotelPriceOnce(
  days: any[],
  dayIndex: number,
  hotelPrice: number,
  seenHotelGroups: Set<string>,
) {
  const day = days[dayIndex];
  if (
    !hotelPrice ||
    day?.hotelIsIncluded ||
    !day?.hotelId ||
    seenHotelGroups.has(`${day.hotelId}_${dayIndex}`)
  ) {
    return 0;
  }

  // Mark every day of this hotel group as seen, so it's only priced once.
  for (let i = dayIndex; i < days.length; i += 1) {
    const currentDay = days[i];
    if (currentDay.hotelId === day.hotelId && !currentDay.hotelIsIncluded) {
      seenHotelGroups.add(`${currentDay.hotelId}_${i}`);
    } else {
      break;
    }
  }
  return hotelPrice;
}

function addCurrencies(
  day: any,
  experiencePricing: any,
  hotelPricing: any,
  currencies: Set<string>,
) {
  if (experiencePricing?.currency) {
    currencies.add(experiencePricing.currency.toLowerCase());
  }
  // A hotel currency only counts with a price: a stay's later nights fall back
  // to the hotel record, priceless and in the hotel's catalog currency, and
  // an unavailable room prices at NaN.
  if (
    hotelPricing?.currency &&
    !day?.hotelIsIncluded &&
    Number.isFinite(parseFloat(hotelPricing.price))
  ) {
    currencies.add(hotelPricing.currency.toLowerCase());
  }
  const dayExtras = Array.isArray(day?.selectedExtras)
    ? day.selectedExtras
    : [];
  dayExtras.forEach((extra: any) => {
    if (extra?.price && extra?.currency) {
      currencies.add(extra.currency.toLowerCase());
    }
  });
}

// Pax comes from `composition`, the trip's aggregate group (KD-3608): a
// reopened trip has no `childrenPax` — the API only stores adultPax and
// childrenBirthdates — so counting on it dropped every child and divided the
// total by the adults alone. A trip opened without any pax split would give
// NaN here, and the per-pax line would silently render empty — keep it at 0
// so callers can skip it.
function participantsFromTripFilters(tripFilters: any): number {
  const composition = tripFilters?.composition;
  const adults = composition?.adults ?? tripFilters?.adultPax ?? 0;
  const children =
    composition?.children ??
    composition?.childrenBirthdates?.length ??
    tripFilters?.childrenBirthdates?.length ??
    0;
  return composition?.count ?? adults + children;
}

/**
 * Global trip price/currency computed from the shared store's
 * days/experiences/hotels. Instance rule is D1: an experience is priced on
 * the first day of *each* instance (`experienceDayIndex` 1 or empty) —
 * placing it twice always prices it twice; whether it *may* be placed twice
 * stays a UI rule in `ComposeDayExperience`, not a pricing one.
 */
export function useTripTotal(): GlobalTripPriceResult {
  const { getHotelPriceOverride } = useTripDesignerConfig();
  const days = useTripStore((state) => state.days);
  const experiences = useTripStore((state) => state.experiences);
  const hotels = useTripStore((state) => state.hotels);
  const tripFilters = useTripStore((state) => state.tripFilters);
  const participants = participantsFromTripFilters(tripFilters);

  const experiencesMap = experiences ?? {};
  const hotelsMap = hotels ?? {};

  const priceLoading = !!days?.some((day) => {
    const hotelLoading = day?.hotelIsLoading === true && !day?.hotelHasError;
    const experienceLoading = day?.priceLoading === true;
    return hotelLoading || experienceLoading;
  });

  let globalTripPrice = 0;
  const currencies = new Set<string>();
  const seenHotelGroups = new Set<string>();

  const allDays = days ?? [];

  allDays.forEach((day, dayIndex) => {
    const experience = day?.experienceId
      ? experiencesMap[day.experienceId]
      : undefined;
    const experiencePricing = resolveExperiencePricing(day, experience);
    const experiencePrice = parseFloat(experiencePricing?.price ?? 0);
    const experienceDayIdx = day?.experienceDayIndex;

    if (day?.experienceId && (experienceDayIdx === 1 || !experienceDayIdx)) {
      globalTripPrice += experiencePrice;
    }

    const hotel = day?.hotelId ? hotelsMap[day.hotelId] : undefined;
    const hotelPricing = resolveHotelPricing(day, hotel, getHotelPriceOverride);
    const hotelPrice = parseFloat(hotelPricing?.price ?? 0);

    globalTripPrice += addHotelPriceOnce(
      allDays,
      dayIndex,
      hotelPrice,
      seenHotelGroups,
    );
    addCurrencies(day, experiencePricing, hotelPricing, currencies);
  });

  const globalTripCurrency =
    currencies.size === 0
      ? null
      : currencies.size > 1
        ? 'mismatch'
        : Array.from(currencies)[0];

  const hasNoProducts = !days?.some((day) => day?.experienceId || day?.hotelId);

  return {
    priceLoading,
    globalTripPrice,
    globalTripCurrency,
    hasCurrencyMismatch: !priceLoading && globalTripCurrency === 'mismatch',
    participants,
    hasNoProducts,
  };
}
